import { lazy, type ComponentType } from "react";
import type { ModuleDef, ModuleId } from "../lib/types";

// Sidebar groups, ordered by workflow with the researcher's own workspace
// first. The Research group keeps a curated order (workspace → corpus entry
// points → data utilities); every other group lists its modules
// alphabetically by name.
export const MODULE_GROUPS: { group: string; items: ModuleDef[] }[] = [
  {
    group: "Start",
    items: [
      {
        id: "home",
        name: "Home",
        group: "Start",
        keywords:
          "home getting started welcome landing overview intro first credits",
      },
    ],
  },
  {
    group: "Research",
    // Curated order (the researcher's own workspace first, then the corpus
    // entry points, then the data utilities) — not alphabetical, by request.
    items: [
      // My Research merges the annotation notebook, collections, the findings
      // tracker, and the research report into one hub (tabs).
      {
        id: "annot",
        name: "My Research",
        group: "Research",
        keywords:
          "annotations notebook collections research report findings export notes",
      },
      {
        id: "lexicon",
        name: "My Lexicon",
        group: "Research",
        keywords: "lexicon glossary meanings annotations dictionary vocabulary",
      },
      { id: "query", name: "Query Builder", group: "Research" },
      {
        id: "commentary",
        name: "Commentary Browser",
        group: "Research",
        keywords:
          "commentary younger ku scholarly browse read inscription notes academia full-text search reference",
      },
      {
        id: "browse",
        name: "Corpus Browser",
        group: "Research",
        keywords: "browse tablets paginate walk by glyph sign index imagery",
      },
      {
        id: "health",
        name: "Corpus Health",
        group: "Research",
        keywords:
          "health dashboard coverage completeness damage uncertain metadata quality missing scribe period image translation",
      },
      { id: "search", name: "Corpus Search", group: "Research" },
      {
        id: "plates",
        name: "Plate Workbench",
        group: "Research",
        keywords:
          "plates facsimile images photographs gorila side by side compare drawing autopsy viewer",
      },
      { id: "wlm", name: "Wordlist Manager", group: "Research" },
      {
        id: "export",
        name: "Data Export",
        group: "Research",
        keywords:
          "export csv json data backup restore wipe reset cache lost recover transfer",
      },
    ],
  },
  {
    group: "Vocabulary",
    items: [
      {
        id: "cooc",
        name: "Co-occurrence",
        group: "Vocabulary",
        keywords: "network graph collocation pmi force-directed collocates",
        category: "descriptive",
      },
      { id: "kwic", name: "Concordance (KWIC)", group: "Vocabulary", category: "descriptive" },
      { id: "lexstats", name: "Lexical Statistics", group: "Vocabulary", category: "descriptive" },
      { id: "minpairs", name: "Minimal Pairs", group: "Vocabulary", category: "descriptive" },
      { id: "morph", name: "Morphology", group: "Vocabulary", category: "exploratory" },
      { id: "ngram", name: "N-grams", group: "Vocabulary", category: "descriptive" },
      { id: "roots", name: "Root Cognates", group: "Vocabulary", category: "exploratory" },
      { id: "stems", name: "Stem Families", group: "Vocabulary", category: "exploratory" },
      { id: "freq", name: "Word Frequency", group: "Vocabulary", category: "descriptive" },
    ],
  },
  {
    group: "Signs & structure",
    items: [
      { id: "compare", name: "Compare Inscriptions", group: "Signs & structure", category: "descriptive" },
      { id: "pos", name: "Positional Grammar", group: "Signs & structure", category: "descriptive" },
      { id: "seqpat", name: "Sequence Patterns", group: "Signs & structure", category: "descriptive" },
      { id: "signs", name: "Sign Concordance", group: "Signs & structure", category: "descriptive" },
      { id: "signref", name: "Sign Inventory", group: "Signs & structure", category: "descriptive" },
      {
        id: "signpat",
        name: "Sign Patterns",
        group: "Signs & structure",
        category: "descriptive",
        keywords: "sign pattern wildcard glob graphotactic search prefix suffix middle",
      },
      { id: "signtrans", name: "Sign Transitions", group: "Signs & structure", category: "descriptive" },
      { id: "similarity", name: "Similarity", group: "Signs & structure", category: "descriptive" },
    ],
  },
  {
    group: "Accounts & content",
    items: [
      {
        id: "dossiers",
        name: "Account Dossiers",
        group: "Accounts & content",
        category: "exploratory",
        keywords:
          "dossier account holder name entries quantities commodities ledger follow person prosopography",
      },
      { id: "arith", name: "Accounting & Metrology", group: "Accounts & content", category: "descriptive" },
      { id: "commodities", name: "Commodity Catalog", group: "Accounts & content", category: "descriptive" },
      {
        id: "doctypes",
        name: "Document Types",
        group: "Accounts & content",
        category: "descriptive",
        keywords:
          "document types support tablet roundel nodule sealing vessel libation bar physical object typology function",
      },
      { id: "lib", name: "Libation Formulas", group: "Accounts & content", category: "descriptive" },
      {
        id: "metrology",
        name: "Metrology Lab",
        group: "Accounts & content",
        category: "descriptive",
        keywords:
          "metrology fractions klasmatogram numerals units measures denominators quantities counted measured",
      },
      { id: "sem", name: "Semantic Classifier", group: "Accounts & content", category: "exploratory" },
      { id: "struct", name: "Tablet Structure", group: "Accounts & content", category: "descriptive" },
    ],
  },
  {
    group: "Hypothesis testing",
    items: [
      { id: "comp", name: "Cross-Linguistic", group: "Hypothesis testing", category: "exploratory" },
      { id: "onomastics", name: "Name Candidates", group: "Hypothesis testing", category: "exploratory" },
      // Sound Shift now folds the live editor + the saved-hypothesis workspace
      // into one tabbed module; `hypws` is kept routable via the alias map so
      // deep-links / Help references still land on the Workspace tab.
      {
        id: "hyp",
        name: "Sound Shift",
        group: "Hypothesis testing",
        keywords:
          "phonetic override hypothesis workspace saved snapshot diff sound shift",
        category: "exploratory",
      },
    ],
  },
  {
    group: "Distribution",
    items: [
      {
        id: "constellation",
        name: "Constellation",
        group: "Distribution",
        category: "exploratory",
        keywords:
          "constellation map embedding correspondence analysis all tablets scatter overview starfield",
      },
      { id: "diachronic", name: "Diachronic (MM/LM)", group: "Distribution", category: "descriptive" },
      // Geography merges the findspot map + site-distribution stats (tabs).
      {
        id: "map",
        name: "Geography",
        group: "Distribution",
        keywords: "findspot map site distribution geographic jaccard overlay",
        category: "descriptive",
      },
      // Scribes merges the per-scribe comparison + scribal network (tabs).
      {
        id: "scribes",
        name: "Scribes",
        group: "Distribution",
        keywords: "scribe comparison scribal network hand site baseline",
        category: "descriptive",
      },
      {
        id: "timeline",
        name: "Timeline",
        group: "Distribution",
        category: "descriptive",
        keywords:
          "timeline chronology periods MM LM protopalatial neopalatial dates ceramic phases history",
      },
    ],
  },
  {
    group: "Learn",
    items: [
      {
        id: "reader",
        name: "Guided Reader",
        group: "Learn",
        keywords:
          "guided reader learn tutorial walkthrough beginner read tablet step by step teach",
      },
      {
        id: "school",
        name: "Scribe School",
        group: "Learn",
        keywords:
          "scribe school practice exercise balance kuro total compute numerals fractions compose ledger sandbox game quiz",
      },
      {
        id: "trainer",
        name: "Sign Trainer",
        group: "Learn",
        keywords:
          "sign trainer flashcards leitner memorize learn signs values practice spaced repetition",
      },
      {
        id: "write",
        name: "Write in Linear A",
        group: "Learn",
        keywords:
          "write your name linear a glyphs syllables transliterate convert render fun",
      },
    ],
  },
  {
    group: "Programmatic",
    items: [
      {
        id: "pyaegean",
        name: "Python Toolkit",
        group: "Programmatic",
        keywords:
          "python pyaegean pip code api jupyter pandas notebook programmatic snippets data round-trip",
      },
    ],
  },
  {
    group: "Help",
    items: [
      { id: "help", name: "How to Use", group: "Help" },
      {
        id: "methodology",
        name: "Methodology",
        group: "Help",
        keywords:
          "methodology math algorithms statistics pmi g2 chi-squared jaccard levenshtein fisher bonferroni wilson zipf phonetic distance citation provenance limitations",
      },
    ],
  },
];

export const ALL_MODULES: ModuleDef[] = MODULE_GROUPS.flatMap((g) => g.items);

// Secondary ids that resolve to a consolidated module's primary sidebar entry.
// Used to keep the sidebar highlight correct when a deep-link / pivot routes to
// a sub-view by its old id (e.g. "collections" → the "My Research" entry).
export const MODULE_ALIASES: Partial<Record<ModuleId, ModuleId>> = {
  network: "cooc",
  geo: "map",
  scribenet: "scribes",
  collections: "annot",
  report: "annot",
  hypws: "hyp",
};

// Consolidated modules: several ids resolve to one tabbed wrapper. The
// "secondary" ids (network, geo, scribenet, collections, report) are kept
// routable so existing deep-links / "Open in" pivots / Help links still work;
// the wrapper reads the active id to open on the right tab.
const CooccurrenceTabs = lazy(() => import("./CooccurrenceTabs"));
const Geography = lazy(() => import("./Geography"));
const Scribes = lazy(() => import("./Scribes"));
const ResearchHub = lazy(() => import("./ResearchHub"));
const SoundShiftTabs = lazy(() => import("./SoundShiftTabs"));

export const MODULE_COMPONENTS: Record<ModuleId, ComponentType> = {
  home: lazy(() => import("./Home")),
  search: lazy(() => import("./CorpusSearch")),
  browse: lazy(() => import("./CorpusBrowser")),
  freq: lazy(() => import("./WordFrequency")),
  morph: lazy(() => import("./Morphology")),
  cooc: CooccurrenceTabs,
  ngram: lazy(() => import("./Ngrams")),
  arith: lazy(() => import("./ArithmeticCheck")),
  signs: lazy(() => import("./SignConcordance")),
  signref: lazy(() => import("./SignInventory")),
  comp: lazy(() => import("./CrossLinguistic")),
  hyp: SoundShiftTabs,
  sem: lazy(() => import("./SemanticClassifier")),
  pos: lazy(() => import("./PositionalGrammar")),
  struct: lazy(() => import("./TabletStructure")),
  lib: lazy(() => import("./LibationFormulas")),
  roots: lazy(() => import("./RootCognates")),
  seqpat: lazy(() => import("./SequencePatterns")),
  geo: Geography,
  hypws: SoundShiftTabs,
  export: lazy(() => import("./DataExport")),
  wlm: lazy(() => import("./WordlistManager")),
  annot: ResearchHub,
  lexicon: lazy(() => import("./MyLexicon")),
  query: lazy(() => import("./QueryBuilder")),
  compare: lazy(() => import("./CompareInscriptions")),
  network: CooccurrenceTabs,
  collections: ResearchHub,
  similarity: lazy(() => import("./Similarity")),
  map: Geography,
  kwic: lazy(() => import("./Concordance")),
  stems: lazy(() => import("./StemFamilies")),
  scribes: Scribes,
  scribenet: Scribes,
  commodities: lazy(() => import("./Commodities")),
  signtrans: lazy(() => import("./SignTransitions")),
  minpairs: lazy(() => import("./MinimalPairs")),
  lexstats: lazy(() => import("./LexicalStats")),
  diachronic: lazy(() => import("./Diachronic")),
  onomastics: lazy(() => import("./Onomastics")),
  report: ResearchHub,
  methodology: lazy(() => import("./Methodology")),
  signpat: lazy(() => import("./SignPatterns")),
  commentary: lazy(() => import("./CommentaryBrowser")),
  pyaegean: lazy(() => import("./PythonToolkit")),
  health: lazy(() => import("./CorpusHealth")),
  doctypes: lazy(() => import("./DocumentTypes")),
  dossiers: lazy(() => import("./AccountDossiers")),
  metrology: lazy(() => import("./MetrologyLab")),
  timeline: lazy(() => import("./Timeline")),
  reader: lazy(() => import("./GuidedReader")),
  school: lazy(() => import("./ScribeSchool")),
  write: lazy(() => import("./WriteLinearA")),
  trainer: lazy(() => import("./SignTrainer")),
  constellation: lazy(() => import("./Constellation")),
  plates: lazy(() => import("./PlateWorkbench")),
  help: lazy(() => import("./Help")),
};
